import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Build a merged mesh group for traffic lights at OSM highway=traffic_signals nodes.
 *
 * Each light consists of:
 *   - A steel pole (~4 m tall)
 *   - A dark housing box at the top
 *   - Three signal spheres: red (on/emissive), yellow (dim), green (dim)
 *
 * @param {Array}    trafficSignals  [{id, lat, lon}]
 * @param {Object}   proj            projection with .project(lat, lon) → {x, z}
 * @param {Function} [elevFn]        elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildTrafficLightsMesh(trafficSignals, proj, elevFn = null) {
  if (!trafficSignals?.length) return new THREE.Group();

  const metalGeos  = [];
  const redGeos    = [];
  const yellowGeos = [];
  const greenGeos  = [];

  for (const ts of trafficSignals) {
    const p = proj.project(ts.lat, ts.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    const y0 = elevFn ? elevFn(p.x, p.z) : 0;

    // Pole: 4 m tall, centre at y0+2.0
    const poleGeo = new THREE.CylinderGeometry(0.05, 0.06, 4.0, 6);
    poleGeo.translate(p.x, y0 + 2.0, p.z);
    metalGeos.push(poleGeo);

    // Housing: 0.22 × 0.72 × 0.14 m, centre at y0+4.36
    const housingGeo = new THREE.BoxGeometry(0.22, 0.72, 0.14);
    housingGeo.translate(p.x, y0 + 4.36, p.z);
    metalGeos.push(housingGeo);

    // Red light (top)
    const redGeo = new THREE.SphereGeometry(0.07, 6, 4);
    redGeo.translate(p.x, y0 + 4.60, p.z);
    redGeos.push(redGeo);

    // Yellow light (middle)
    const yellowGeo = new THREE.SphereGeometry(0.07, 6, 4);
    yellowGeo.translate(p.x, y0 + 4.36, p.z);
    yellowGeos.push(yellowGeo);

    // Green light (bottom)
    const greenGeo = new THREE.SphereGeometry(0.07, 6, 4);
    greenGeo.translate(p.x, y0 + 4.12, p.z);
    greenGeos.push(greenGeo);
  }

  const group = new THREE.Group();

  if (metalGeos.length) {
    const merged = mergeGeometries(metalGeos);
    metalGeos.forEach(g => g.dispose());
    group.add(new THREE.Mesh(merged,
      new THREE.MeshLambertMaterial({ color: 0x3a3e45 })));
  }

  if (redGeos.length) {
    const merged = mergeGeometries(redGeos);
    redGeos.forEach(g => g.dispose());
    group.add(new THREE.Mesh(merged,
      new THREE.MeshLambertMaterial({
        color:             0xff2200,
        emissive:          new THREE.Color(0xff1100),
        emissiveIntensity: 0.35,
      })));
  }

  if (yellowGeos.length) {
    const merged = mergeGeometries(yellowGeos);
    yellowGeos.forEach(g => g.dispose());
    group.add(new THREE.Mesh(merged,
      new THREE.MeshLambertMaterial({ color: 0x443300 })));
  }

  if (greenGeos.length) {
    const merged = mergeGeometries(greenGeos);
    greenGeos.forEach(g => g.dispose());
    group.add(new THREE.Mesh(merged,
      new THREE.MeshLambertMaterial({ color: 0x003314 })));
  }

  return group;
}
